import { iconSize, px } from '@/utils/hubScale'
import React, { useCallback, useMemo } from 'react'
import { notification } from 'antd'
import styled from 'styled-components'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ProjectFileUsage } from '@autoriz/shared/files'
import { AccountType, ProjectFileSection, ProjectStep } from 'src/utils/enums'
import { usePermissions } from '@/hooks/auth/usePermissions'
import {
  ProjectFileUsageMeta,
  isFileUsageVisible,
  isFileUsageEditable,
} from 'src/utils/projectFiles'
import { SimpleUploadButton } from '@features/file-upload/components/UploadButton'
import { DOCUMENTS_VIEW_QUERY_KEY } from 'src/features/documents/hooks/useDocumentsView'
import type { FileView } from 'src/features/documents/types/documents.types'
import { UsageFileTable } from 'src/features/documents/components/UsageFileTable'
import { useHubProjectDetail } from '@features/projects/hub/hooks/useHubProjectDetail'
import {
  isCountedSlotFile,
  useProjectHubDocumentSlots,
} from '@features/projects/hub/hooks/useProjectHubDocumentSlots'
import { misplacedDocumentSlot } from '@features/projects/hub/services/misplacedDocument'
import {
  getProjectFilesOfUsages,
  type ProjectFilesByUsage,
} from 'src/services/gql/project_files'
import { GQL_CLIENT } from 'src/graphqlClient'

const COLORS = {
  ink400: '#8a8980',
  ink500: '#65645e',
  ink700: '#353430',
  ink900: '#14130f',
}

const Wrapper = styled.div`
  margin: 0;
  font-family: 'Geist', 'Inter', system-ui, sans-serif;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${px(12)};
  margin: ${px(28)} 0 ${px(8)};
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${px(10)};
`

const HeaderTitle = styled.div`
  font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif;
  font-size: ${px(18)};
  font-weight: 600;
  color: ${COLORS.ink900};
`

const Sub = styled.div`
  font-size: ${px(13)};
  color: ${COLORS.ink500};
  margin-bottom: ${px(12)};
`

/**
 * The whole section, while it is empty, as one link (#668).
 *
 * A heading, a subtitle, an upload button and an "Aucun fichier déposé." table
 * — four lines of chrome for an action that is optional and rare, sitting under
 * the pieces that are neither. Once a file is in it the full section renders,
 * because then it has something to show.
 */
const EmptyRow = styled.div`
  margin: ${px(24)} 0 0;
  font-family: 'Geist', 'Inter', system-ui, sans-serif;

  .ant-btn {
    padding: 0;
    height: auto;
    font-size: ${px(13.5)};
    font-weight: 600;
    color: ${COLORS.ink700};
  }

  .ant-btn:hover {
    color: ${COLORS.ink900};
  }
`

const EmptyLinkHint = styled.span`
  color: ${COLORS.ink400};
  font-weight: 400;
`

const DocIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    style={iconSize(20)}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M9 13h6" />
    <path d="M9 17h6" />
  </svg>
)

export const OPTIONAL_DOCS_QUERY_KEY = (projectID: number) =>
  ['salesman-optional-docs', projectID] as const

export type OptionalDocumentSectionProps = {
  projectId: number
}

// Map the raw `project_files` shape into the `FileView[]` UsageFileTable
// expects. Mirrors the mapping useDocumentsView does in
// `buildGroupsFromPayload`.
const toFileViews = (rows: ReadonlyArray<ProjectFilesByUsage>): FileView[] =>
  rows.flatMap((r) => {
    if (!r.file) return []
    const status =
      r.status === 'ACCEPTED' || r.status === 'REJECTED' ? r.status : null
    return [
      {
        id: r.file.id,
        name: r.file.name,
        mime_type: r.file.mime_type,
        size_bytes: r.file.size,
        modified_at: r.file.updated_at,
        // The query orders by created_at, so it is selected; fall back to
        // updated_at rather than let a missing value reorder the list.
        created_at: r.file.created_at ?? r.file.updated_at,
        status,
        usage: r.use,
        origin: 'project' as const,
        comment: r.comment ?? undefined,
      },
    ]
  })

/**
 * "Autres documents" section. Lists the files of the OTHER_DOCUMENTS section
 * that are visible to the viewer at the current step — derived from
 * `ProjectFileUsageMeta` (config-driven), not a hardcoded usage. For the
 * salesman that resolves to `UNDEFINED` ("Autres documents"); INTERNALS stays
 * architect-only.
 *
 * Note: `UNDEFINED` is a real category here, distinct from the *unassigned*
 * inbox (`use = NULL`, surfaced by the triage banner). `useDocumentsView`
 * still skips it as a standalone group on the salesman row list, so this
 * dedicated section is the salesman's view of it — fetched directly via
 * `getProjectFilesOfUsages` and mapped onto the shared `FileView` shape.
 *
 * Composes the shared components used by the architect-side /documents route:
 *   - `<SimpleUploadButton usage="UNDEFINED" multiple />` for upload
 *   - `<UsageFileTable />` for the file list
 *
 * Editability is config-driven (`isFileUsageEditable`): the salesman may
 * add/replace these only while the request is in their hands — at intake
 * (PROJECT_INITIALIZED) or after a rejection (PROJECT_REFUSED); every other
 * step is read-only (the upload button hides and `UsageFileTable.editable=false`
 * disables the row-level remove action). Mirrors the backend `files` update
 * permission window for SALESMAN.
 */
export const OptionalDocumentSection: React.FC<
  OptionalDocumentSectionProps
> = ({ projectId }) => {
  const queryClient = useQueryClient()
  const { project } = useHubProjectDetail(projectId)
  const { data: accountType } = usePermissions<AccountType>()
  const step = project?.step as ProjectStep | undefined
  // The mandatory slots, so a file dropped here can be checked against the
  // pieces the dossier still owes — see `handleUploaded`.
  const { slots } = useProjectHubDocumentSlots(projectId)

  // The OTHER_DOCUMENTS-section usages the viewer may see at this step
  // (config-driven). For the salesman this is just UNDEFINED.
  const visibleUsages = useMemo<ProjectFileUsage[]>(
    () =>
      (
        Object.entries(ProjectFileUsageMeta) as Array<
          [ProjectFileUsage, (typeof ProjectFileUsageMeta)[ProjectFileUsage]]
        >
      )
        .filter(
          ([, meta]) => meta?.section === ProjectFileSection.OTHER_DOCUMENTS
        )
        .filter(([usage]) =>
          accountType && step
            ? isFileUsageVisible(accountType, step, usage)
            : true
        )
        .map(([usage]) => usage),
    [accountType, step]
  )

  const { data: rows = [] } = useQuery({
    queryKey: OPTIONAL_DOCS_QUERY_KEY(projectId),
    queryFn: () =>
      getProjectFilesOfUsages(GQL_CLIENT, projectId, visibleUsages),
    enabled: projectId > 0 && visibleUsages.length > 0,
  })
  const files = useMemo(() => toFileViews(rows), [rows])

  // Upload target is the UNDEFINED ("Autres documents") category; editable per
  // the config matrix (before production starts), read-only after.
  const editable =
    accountType && step
      ? isFileUsageEditable(accountType, step, ProjectFileUsage.UNDEFINED)
      : false

  const invalidate = useCallback(() => {
    // Refetch all three caches that surface salesman file state:
    //  - OPTIONAL_DOCS_QUERY_KEY: this section's own list
    //  - DOCUMENTS_VIEW_QUERY_KEY: the shared documents view (used by
    //    the /documents page and the docs-count badge in the section
    //    head row)
    //  - project-status: feeds the section pills + send-gate via
    //    `useHubProjectStatus`
    queryClient.invalidateQueries({
      queryKey: OPTIONAL_DOCS_QUERY_KEY(projectId),
    })
    queryClient.invalidateQueries({
      queryKey: DOCUMENTS_VIEW_QUERY_KEY(projectId),
    })
    queryClient.invalidateQueries({ queryKey: ['project-status', projectId] })
  }, [queryClient, projectId])

  const handleChange = useCallback(() => invalidate(), [invalidate])

  // #668 — a mandatory piece filed here stays counted as missing, because this
  // category is optional. The upload still goes through (the commercial's file
  // is theirs to place), but the section it belongs in is named, so the dead
  // end "I uploaded the procuration and the dossier still won't send" becomes a
  // sentence they can act on.
  const handleUploaded = useCallback(
    (result?: { success?: boolean; filename?: string }) => {
      invalidate()
      if (!result?.success || !result.filename) return
      const owed = slots
        .filter(
          (slot) =>
            slot.required > 0 &&
            slot.files.filter(isCountedSlotFile).length < slot.required,
        )
        .map((slot) => ({
          usage: slot.definition.usage,
          title: slot.definition.title,
        }))
      const misplaced = misplacedDocumentSlot(result.filename, owed)
      if (!misplaced) return
      notification.warning({
        message: 'Cette pièce est peut-être au mauvais endroit',
        description:
          `« ${result.filename} » a été déposé dans « Autres documents », qui ` +
          `est optionnel. Si c'est la pièce demandée, ajoutez-la aussi dans ` +
          `« ${misplaced.title} » — sinon elle restera comptée comme manquante.`,
        duration: 12,
      })
    },
    [invalidate, slots],
  )

  // Same read-back rule the per-usage rows follow: once the request has left the
  // salesman's hands this section is a read-back of what was provided, so an
  // empty one has nothing to say. It cannot ask the rule model with its files —
  // the fetch depends on the visibility answer — hence the explicit guard.
  if (!editable && files.length === 0) return null

  // An empty, optional category is one link, not a four-line section (#668).
  // The "Aucun fichier déposé." row went with it: an empty list is visibly
  // empty.
  if (files.length === 0) {
    return (
      <EmptyRow data-cy="salesman-hub-optional-docs">
        <SimpleUploadButton
          projectId={projectId}
          usage="UNDEFINED"
          multiple
          onUploadComplete={handleUploaded}
          buttonProps={{
            type: 'link',
            icon: null,
            'data-cy': 'salesman-hub-optional-docs--add',
          }}
        >
          + Ajouter un autre document{' '}
          <EmptyLinkHint>(optionnel)</EmptyLinkHint>
        </SimpleUploadButton>
      </EmptyRow>
    )
  }

  return (
    <Wrapper data-cy="salesman-hub-optional-docs">
      <Header>
        <HeaderLeft>
          <DocIcon />
          <HeaderTitle>Autres documents</HeaderTitle>
        </HeaderLeft>
        {editable && (
          <SimpleUploadButton
            projectId={projectId}
            usage="UNDEFINED"
            multiple
            onUploadComplete={handleUploaded}
          >
            Ajouter un document
          </SimpleUploadButton>
        )}
      </Header>
      <Sub>Documents complémentaires — PDF, Word, etc. Optionnel.</Sub>
      <UsageFileTable
        files={files}
        editable={editable}
        projectID={projectId}
        onChange={handleChange}
        // Salesman cannot validate/reject — only the architect sets
        // file status. Hides the inline "Valider"/"Rejeter" buttons
        // on PENDING rows.
        canSetStatus={false}
      />
    </Wrapper>
  )
}
